import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
  Chip,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PropTypes from "prop-types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

const getInitials = (name = "") => {
  const initials = String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return initials.toUpperCase() || "S";
};

const normalizeMediaUrl = (value) => {
  const url = String(value || "").trim();

  if (!url || /^(https?:|blob:|data:)/i.test(url)) {
    return url;
  }

  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(url, `${origin}/`).href;
  } catch {
    return url;
  }
};

const normalizeLinkedinUrl = (value) => {
  const url = String(value || "").trim();

  if (!url) {
    return "";
  }

  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const SkillProviderCard = ({ provider, onDetail, onSelect }) => {
  const providerName = provider.name || provider.full_name || "Skill Provider";
  const imageUrl = normalizeMediaUrl(provider.photo || provider.image);
  const linkedinUrl = normalizeLinkedinUrl(provider.linkedin);
  const description =
    provider.description ||
    `Years of experience: ${provider.years_of_experience || 0} years.`;

  const skill = String(provider.skills || provider.skill || "").trim();

  return (
    <Card
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 320,
        borderRadius: "20px",
        bgcolor: "#fff",
        boxShadow:
          "0px 73px 44px rgba(0, 0, 0, 0.05), 0px 130px 52px rgba(0, 0, 0, 0.01), 0px 204px 57px rgba(0, 0, 0, 0)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow:
            "0px 90px 50px rgba(0, 0, 0, 0.08), 0px 150px 60px rgba(0, 0, 0, 0.03), 0px 220px 65px rgba(0, 0, 0, 0)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        sx={{
          height: 190,
          bgcolor: "#F6F6F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2.25,
        }}
      >
        <Avatar
          src={imageUrl || undefined}
          alt={providerName}
          sx={{
            width: 168,
            height: 168,
            bgcolor: "#292E5C",
            color: "#fff",
            border: "4px solid #fff",
            boxShadow: "0px 10px 28px rgba(17, 24, 39, 0.14)",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            fontSize: 34,
            "& img": {
              objectFit: "cover",
              objectPosition: "center",
            },
          }}
        >
          {!imageUrl ? getInitials(providerName) : null}
        </Avatar>
      </Box>

      {linkedinUrl ? (
        <IconButton
          component="a"
          href={linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${providerName} LinkedIn profile`}
          sx={{
            position: "absolute",
            top: 13,
            right: 13,
            width: 30,
            height: 30,
            bgcolor: "#fff",
            color: "#0A66C2",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            "&:hover": {
              bgcolor: "#dadada",
            },
            }}
        >
          <LinkedInIcon sx={{ fontSize: 22 }} />
        </IconButton>
      ) : null}

      <CardContent
        sx={{
          px: "14px",
          pt: "12px",
          pb: "14px !important",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flex: 1,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Typography
            sx={{
              color: "#000",
              fontSize: 16,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              lineHeight: "20px",
              letterSpacing: "0.3px",
            }}
          >
            {providerName}
          </Typography>

          <Typography
            sx={{
              color: "#555555",
              fontSize: 11,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              lineHeight: "15px",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>

          {/* Skills Section */}
          {skill ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: 11,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                }}
              >
                Skills
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                }}
              >
                <Chip
                  label={skill}
                  size="small"
                  sx={{
                    height: "auto",
                    px: "8px",
                    py: "4px",
                    bgcolor: "#F3F4F6",
                    border: "1px solid #D1D5DB",
                    color: "#55636D",
                    fontSize: "10px",
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    borderRadius: "4px",
                    "& .MuiChip-label": {
                      px: 0,
                    },
                  }}
                />
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={() => onDetail(provider)}
            sx={{
              px: "14px",
              py: "10px",
              bgcolor: "#F7AA47",
              color: "#292E5C",
              borderRadius: "8px",
              borderColor: "#F7AA47",
              boxShadow: "none",
              textTransform: "none",
              fontSize: 12,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              lineHeight: "14px",
              letterSpacing: "0.4px",
              minWidth: "auto",
              "&:hover": {
                bgcolor: "#ff9100",
                borderColor: "#F7AA47",
              },
            }}
          >
            Detail
          </Button>

          <Button
            type="button"
            variant="contained"
            onClick={() => onSelect(provider)}
            sx={{
              px: "16px",
              py: "11px",
              bgcolor: "#292E5C",
              color: "#fff",
              borderRadius: "8px",
              border: "none",
              boxShadow: "none",
              textTransform: "none",
              fontSize: 12,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              lineHeight: "14px",
              letterSpacing: "0.4px",
              transition: "all 0.2s ease-in-out",
              minWidth: "auto",
              "&:hover": {
                bgcolor: "#20264F",
                boxShadow: "0px 6px 16px rgba(41, 46, 92, 0.2)",
                transform: "scale(1.02)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            Select
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

SkillProviderCard.propTypes = {
  provider: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    full_name: PropTypes.string,
    photo: PropTypes.string,
    image: PropTypes.string,
    linkedin: PropTypes.string,
    description: PropTypes.string,
    years_of_experience: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    skill: PropTypes.string,
    skills: PropTypes.string,
  }).isRequired,
  onDetail: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default SkillProviderCard;
